<template>
  <div ref="wrapper" class="grid grid-rows-[1fr_auto] w-full h-full overflow-hidden">
    <div class="flex flex-col w-full min-h-0 overflow-y-auto">
      <!--    Images    -->
      <Carousel
        ref="carousel"
        v-model="currentDrawerImage"
        :itemsToShow="2"
        :wrapAround="false">
        <Slide v-for="image of (selectedItem.images ?? [])" :key="image">
          <img :src="image" alt="image of the product" class="w-full">
        </Slide>
        <template #addons>
          <Pagination />
        </template>
      </Carousel>

      <!--   Title     -->
      <div class="w-full flex flex-col gap-2 justify-start items-start mt-4 px-4">
        <span class="text-sm text-gray-400 ">{{ selectedItem.categories.join(', ') }}</span>
        <div class="flex justify-between items-center w-full">
          <div class="text-xl">
            <span v-if="!selectedItem.discountedPrice" class="font-medium">₪{{ selectedItem.price }}</span>
            <div v-else class="flex gap-2 items-center">
              <span class="font-medium text-purple-400">₪{{ selectedItem.discountedPrice }}</span>
              <span class="text-base line-through">₪{{ selectedItem.price }}</span>
            </div>
          </div>
          <span
            class="text-gray-400 text-sm font-medium">{{
              selectedItem?.availableStock && selectedItem.availableStock > 0 ? t('product.inStock', { count: selectedItem.availableStock }) : t('product.outOfStock')
            }} </span>
        </div>
      </div>

      <div class="w-full flex flex-col px-4 mt-4 gap-1">
        <span class="font-medium text-lg text-black">{{ t('product.description') }}</span>
        <p>{{ selectedItem.description }}</p>
      </div>
    </div>

    <div class="px-4 py-8 mt-6" :style="`background: ${colors.lighterColor}`">
      <flow-button
        v-if="showAddToCart"
        :label="isSelected ? t('product.remove') : t('product.addToCart')"
        :disable="selectedItem.availableStock === 0"
        :colors="colors"
        @click="addToCart" />
    </div>
  </div>
</template>

<script setup lang="ts">

import { Carousel, Pagination, Slide } from 'vue3-carousel';
import { PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IShopItem } from '@torim-app/types';
import FlowButton from './FlowButton.vue';

const fallbackStrings: Record<string, string> = {
  'product.description': 'תיאור המוצר',
  'product.remove': 'הסרה',
  'product.addToCart': 'הוספה לסל',
  'product.outOfStock': 'אזל מהמלאי',
};

let t: (key: string, named?: Record<string, unknown>) => string;
try {
  t = useI18n().t as typeof t;
} catch {
  t = (key: string, named?: Record<string, unknown>) => {
    if (key === 'product.inStock') return `${named?.count ?? ''} במלאי`;
    return fallbackStrings[key] ?? key;
  };
}

const emit = defineEmits(['addToCart']);
const props = defineProps({
  showAddToCart: {
    type: Boolean,
    required: false,
    default: false
  },
  selectedItem: {
    type: Object as PropType<IShopItem>,
    required: true
  },
  isSelected: {
    type: Boolean,
    required: false,
    default: false
  },
  colors: {
    type: Object as PropType<{
      lighterColor: string;
      leadColor: string;
      fontColor: string;
      darkenColor: string;
    }>,
    required: false,
    default: () => ({
      lighterColor: '#d6d6d6',
      leadColor: '#000000',
      fontColor: '#fff',
      darkenColor: '#000000',
    })
  }
});

const currentDrawerImage = ref<number>(0);
const lastElem = ref<HTMLDivElement>();
const wrapper = ref<HTMLDivElement>();

const addToCart = () => {
  emit('addToCart');
};

watch(() => lastElem.value, (value, oldValue) => {
  if (!oldValue && value && lastElem.value) {
    lastElem.value.scrollIntoView({ behavior: 'smooth' });
  }
});

</script>

<style scoped lang="scss">
</style>
